import { ReactNode, useEffect, useMemo, useState, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import './DateRangePopup.css'; // Keep custom CSS
import 'react-datepicker/dist/react-datepicker.css';
import { enGB } from 'date-fns/locale';

import Button from '../Buttons';
import Typography from '../Typography';

import { TimeEditor } from './TimeEditor';
import {
  dateStringToDate,
  dateToDateString,
  dateToTimeParts,
  TimeParts,
  timePartToDate,
  validateRanges,
} from './helpers';
import MaskedDateInput from './MaskedDateInput';

type Dates = [Date | undefined, Date | undefined];

export default function DateRangePopup({
  onClose,
  onSubmit,
  startDateStr,
  endDateStr,
}: {
  onClose: () => void;
  onSubmit: (startDate: Date, endDate: Date) => void;
  startDateStr: string;
  endDateStr: string;
}) {
  const { s, e, st, et /*sd, ed*/ } = useMemo(() => {
    const s = new Date(startDateStr);
    const e = new Date(endDateStr);
    const st = dateToTimeParts(s);
    const et = dateToTimeParts(e);
    const sd = dateToDateString(s);
    const ed = dateToDateString(e);

    return { s, e, st, et, sd, ed };
  }, [startDateStr, endDateStr]);

  const [startEditTime, setStartEditTime] = useState<TimeParts | undefined>(st);
  const [endEditTime, setEndEditTime] = useState<TimeParts | undefined>(et);
  const [calenderDates, setCalendarDates] = useState<Dates>([s, e]);

  const [startEditDate, setStartEditDate] = useState(dateToDateString(calenderDates[0]) || '');
  const [endEditDate, setEndEditDate] = useState(dateToDateString(calenderDates[1]) || '');

  const [errors, setErrors] = useState<{
    sd?: string;
    st?: string;
    et?: string;
    ed?: string;
  }>({});

  useEffect(() => {
    setStartEditDate(dateToDateString(calenderDates[0]) || '');
    setEndEditDate(dateToDateString(calenderDates[1]) || '');
  }, [calenderDates]);

  function onCalendarChange(dates: Dates) {
    setCalendarDates(dates);
  }

  const combinedStart =
    startEditDate && startEditTime
      ? timePartToDate(dateStringToDate(startEditDate) as Date, startEditTime)
      : undefined;

  const combinedEnd =
    endEditDate && endEditTime
      ? timePartToDate(dateStringToDate(endEditDate) as Date, endEditTime)
      : undefined;

  const isEditDateRangeComplete =
    startEditDate
      .split('/')
      .every(
        (part, i) => (i === 0 ? part.length === 4 : part.length === 2) && !part.includes('_')
      ) &&
    endEditDate
      .split('/')
      .every((part, i) => (i === 0 ? part.length === 4 : part.length === 2) && !part.includes('_'));

  const rangeError = isEditDateRangeComplete
    ? validateRanges(combinedStart, combinedEnd)
    : undefined;

  const hasError =
    !!Object.keys(errors || {}).filter((k) => !!errors?.[k as keyof NonNullable<typeof errors>])
      ?.length || !!rangeError;

  const valid = isEditDateRangeComplete && !!combinedEnd && !hasError;

  const handleSubmit = () => {
    onSubmit(combinedStart!, combinedEnd!);
  };

  const handleError = useCallback(
    (err: string | undefined, key: keyof NonNullable<typeof errors>) => {
      setErrors((prev) => {
        if (prev[key] === err) {
          return prev; // Avoid state update if error is the same
        }
        const newErrors = { ...prev };
        newErrors[key] = err;
        return newErrors;
      });
    },
    [] // setErrors is stable, so no dependencies needed here
  );

  const setStartDateError = useCallback(
    (err: string | undefined) => handleError(err, 'sd'),
    [handleError]
  );
  const setStartTimeError = useCallback(
    (err: string | undefined) => handleError(err, 'st'),
    [handleError]
  );
  const setEndDateError = useCallback(
    (err: string | undefined) => handleError(err, 'ed'),
    [handleError]
  );
  const setEndTimeError = useCallback(
    (err: string | undefined) => handleError(err, 'et'),
    [handleError]
  );

  return (
    <div className="shadow-datepicker w-[680px] h-fit px-[20px] pt-[20px] flex flex-col bg-white rounded-md gap-[20px]">
      <div className="flex flex-1 w-full gap-3">
        <div className="w-[308px] px-[10px] flex flex-col justify-between">
          <EditorSection title="Start Date">
            <MaskedDateInput
              value={startEditDate}
              onInternalValueChange={(v) => setStartEditDate(v)}
              onCalendarDateChange={(v) =>
                hasError ? null : setCalendarDates((prev) => [dateStringToDate(v), prev[1]])
              }
              error={errors?.sd}
              setError={setStartDateError}
            />
            <TimeEditor
              onChange={setStartEditTime}
              initialValue={st}
              error={errors?.st}
              setError={setStartTimeError}
            />
          </EditorSection>
          <EditorSection title="End Date">
            <MaskedDateInput
              value={endEditDate}
              onInternalValueChange={(v) => setEndEditDate(v)}
              onCalendarDateChange={(v) => {
                hasError ? null : setCalendarDates((prev) => [prev[0], dateStringToDate(v)]);
              }}
              error={errors?.ed}
              setError={setEndDateError}
            />
            <TimeEditor
              onChange={setEndEditTime}
              initialValue={et}
              error={errors?.et || rangeError}
              setError={setEndTimeError}
            />
          </EditorSection>
        </div>
        <div className="flex-1">
          <DatePicker
            inline
            locale={enGB}
            selectsRange
            maxDate={new Date()}
            selected={calenderDates[0]}
            startDate={calenderDates[0]}
            endDate={calenderDates[1]}
            onChange={(dates) => onCalendarChange([dates[0] || undefined, dates[1] || undefined])}
            disabledKeyboardNavigation
            fixedHeight
          />
        </div>
      </div>
      <div className="h-[84px] border-t border-t-primary-70 flex items-center justify-between">
        <Button variant="secondary" className="h-[44px] w-[94px] border-none" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="h-[44px] w-[94px]"
          variant="primary"
          disabled={!valid}
          onClick={handleSubmit}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function EditorSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <Typography variant="p2">{title}</Typography>
      {children}
    </div>
  );
}
